import { useState, useEffect } from "react";
import { useLeague } from "./LeagueLayout";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import LeagueSponsorsAdmin from "../../components/LeagueSponsorsAdmin";
import FinalizeSeasonDialog from "../../components/FinalizeSeasonDialog";
import ModuliRegoleDialog from "../../components/ModuliRegoleDialog";
import AutoSnapshotDialog, { suggestMilestoneForPhase } from "../../components/AutoSnapshotDialog";
import {
  Settings, Upload, Trophy, Sparkles, Flag, Layers, Users, ScrollText, FileSpreadsheet,
  ArrowRight, ChevronRight, Zap, Info, RotateCcw, RefreshCcw, UploadCloud, X, CheckCircle2, Puzzle
} from "lucide-react";

/**
 * Hub "Decisioni Admin" — centralizza tutte le funzioni admin della lega.
 * L'admin trova qui tutto ciò che non fa parte dell'esperienza manager.
 */
export default function LeagueDecisioniAdmin() {
  const { league, isAdmin, reload } = useLeague();
  const nav = useNavigate();
  const [section, setSection] = useState("overview"); // overview | sponsor | fase
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [phaseBusy, setPhaseBusy] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [moduliOpen, setModuliOpen] = useState(false);
  const [autoSnapshotOpen, setAutoSnapshotOpen] = useState(false);

  if (!isAdmin) {
    return (
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-400" data-testid="not-admin">
        Questa sezione è riservata agli admin di lega.
      </div>
    );
  }

  const phase = league.season_phase ?? 3;
  const nextPhase = phase < 14 ? phase + 1 : 1;

  const doAdvance = async () => {
    setPhaseBusy(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/phase/advance`);
      const info = r.data.current_info;
      toast.success(`${info.emoji} Nuova fase: ${info.label}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPhaseBusy(false); setAutoSnapshotOpen(false); }
  };

  const advance = () => {
    if (suggestMilestoneForPhase(phase)) {
      setAutoSnapshotOpen(true);
    } else {
      if (!window.confirm(`Avanzare alla fase successiva?`)) return;
      doAdvance();
    }
  };

  const rollback = async () => {
    if (!window.confirm(`⚠️ Tornare indietro alla fase precedente? Usa solo in caso di errore.`)) return;
    setPhaseBusy(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/phase/rollback`);
      const info = r.data.current_info;
      toast.success(`↩️ Tornato a: ${info.emoji} ${info.label}`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPhaseBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="decisioni-admin-page">
      {/* Hero */}
      <div className="rounded-3xl border border-amber-500/40 bg-gradient-to-br from-slate-900 via-slate-950 to-amber-950/30 p-6 sm:p-7 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-amber-500/10 blur-3xl pointer-events-none"/>
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-rose-500/10 blur-3xl pointer-events-none"/>
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
            <Settings className="text-amber-400" size={22}/>
          </div>
          <div className="flex-1">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">Decisioni Admin</h1>
            <p className="text-sm text-slate-300 mt-1">
              Da qui gestisci la lega senza appesantire la vista manager. Ogni sezione è un&apos;area operativa dedicata.
            </p>
          </div>
        </div>
      </div>

      {section === "overview" && (
        <>
          {/* Fase corrente */}
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 sm:p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <Layers className="text-amber-400" size={18}/>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Fase corrente</div>
                  <div className="text-lg font-black text-white">Fase {phase}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={rollback} disabled={phaseBusy || phase <= 0}
                  data-testid="phase-rollback-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest border border-slate-700 text-slate-300 hover:border-rose-500/60 hover:text-rose-300 disabled:opacity-30">
                  <RotateCcw size={13}/> Rollback
                </button>
                <button onClick={advance} disabled={phaseBusy}
                  data-testid="phase-advance-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-900 hover:brightness-110 disabled:opacity-30 shadow-lg shadow-emerald-500/20">
                  Avanza fase <ChevronRight size={13}/>
                </button>
              </div>
            </div>
          </div>

          {/* Grid di tiles */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Tile icon={Sparkles} color="#f59e0b" title="Sponsor" desc="Attiva/rimuovi sponsor, toggle caroselli, finalizza stagione"
              onClick={() => setSection("sponsor")} testid="tile-sponsor"/>
            <Tile icon={Trophy} color="#10b981" title="Classifica" desc="Importa da Excel o URL Fantacalcio.it, mappa squadre"
              onClick={() => nav(`/lega/${league.league_id}/classifica?admin=1`)} testid="tile-classifica"/>
            <Tile icon={Upload} color="#3b82f6" title="Import rose e dati" desc="Rose iniziali, quotazioni, calendario, diff mercato"
              onClick={() => nav(`/lega/${league.league_id}/import`)} testid="tile-import"/>
            <Tile icon={Users} color="#8b5cf6" title="Gestione lega" desc="Co-admin, impostazioni, tema colore, logo"
              onClick={() => nav(`/lega/${league.league_id}/admin`)} testid="tile-admin"/>
            <Tile icon={ScrollText} color="#64748b" title="Log operazioni" desc="Cronologia di tutte le azioni admin della lega"
              onClick={() => nav(`/lega/${league.league_id}/log`)} testid="tile-log"/>
            <Tile icon={Flag} color="#ef4444" title="Finalizza stagione" desc="Bulk-close di tutti gli sponsor con classifica finale"
              onClick={() => setFinalizeOpen(true)} testid="tile-finalize"/>
            <Tile icon={ScrollText} color="#f59e0b" title="🩺 Salute Rosa" desc="Risolvi i giocatori marcati 'Da verificare' (senza fanta_id o mancanti dal file rose). Collegali al listone con top-3 candidati fuzzy o via AI."
              onClick={() => nav(`/lega/${league.league_id}/salute-rosa`)} testid="tile-salute-rosa"/>
            <Tile icon={RotateCcw} color="#a855f7" title="♻️ Backup & Ripristino Rose" desc="Snapshot auto salvati prima di ogni import — ripristina in un click"
              onClick={() => setSnapshotsOpen(true)} testid="tile-snapshots"/>
            <Tile icon={Puzzle} color="#06b6d4" title="🧩 Moduli & Regole" desc="Attiva/disattiva regole custom della lega: economia, sponsor, AI, divisioni. Applica preset o modifica singoli toggle"
              onClick={() => setModuliOpen(true)} testid="tile-moduli"/>
            <Tile icon={ScrollText} color="#0891b2" title="📓 Diario Mercato" desc="Cronologia dei trasferimenti reali Serie A + cambi listing (LISTED_ACTIVE / TEMPORARILY_UNLISTED / LISTED_CEDUTO / RELISTED)"
              onClick={() => nav(`/lega/${league.league_id}/diario-mercato`)} testid="tile-diario-mercato"/>
            <Tile icon={ScrollText} color="#8b5cf6" title="🏁 Milestones" desc="Snapshot semantici della stagione: Rose Iniziali · Pre/Post Riparazione · Fine Campionato · Pre/Post Asta Furti"
              onClick={() => nav(`/lega/${league.league_id}/milestones`)} testid="tile-milestones"/>
            <Tile icon={FileSpreadsheet} color="#f59e0b" title="📥 Template Excel" desc="Scarica il template XLSX pre-compilato con tutte le squadre e rose attuali. Struttura identica al Post-Calendario."
              onClick={async () => {
                try {
                  const r = await api.get(`/leagues/${league.league_id}/imports/template.xlsx`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([r.data]));
                  const a = document.createElement("a"); a.href = url; a.download = `template_${league.name.replace(/\s+/g,"_")}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                  toast.success("Template scaricato");
                } catch (e) { toast.error("Errore download template"); }
              }} testid="tile-template-download"/>
            <Tile icon={FileSpreadsheet} color="#eab308" title="📄 Template Vuoto (Nuova Stagione)" desc="Scarica un template XLSX blank con placeholder per ogni squadra (25 righe). Ideale per iniziare una nuova stagione da zero."
              onClick={async () => {
                try {
                  const r = await api.get(`/leagues/${league.league_id}/imports/template.xlsx?empty=true`, { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([r.data]));
                  const a = document.createElement("a"); a.href = url; a.download = `template_vuoto_${league.name.replace(/\s+/g,"_")}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                  toast.success("Template vuoto scaricato");
                } catch (e) { toast.error("Errore download template vuoto"); }
              }} testid="tile-template-empty-download"/>
          </div>

          <div className="rounded-xl bg-slate-900/50 border border-slate-800 p-3 flex items-start gap-2 text-xs text-slate-400" data-testid="admin-tip">
            <Info size={14} className="text-amber-400 mt-0.5 shrink-0"/>
            <div>
              Le tab principali ora mostrano solo l&apos;esperienza <span className="font-bold text-white">manager</span>. Qui gestisci le decisioni riservate all&apos;admin. Ogni link ti porta alla sezione dedicata con i comandi completi.
            </div>
          </div>
        </>
      )}

      {section === "sponsor" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setSection("overview")}
              data-testid="back-overview"
              className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-white uppercase tracking-widest">
              ← Torna alle decisioni
            </button>
            <button onClick={() => setFinalizeOpen(true)}
              data-testid="open-finalize-dialog"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-[0.2em] bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 hover:brightness-110 shadow-lg shadow-amber-500/20">
              <Flag size={13}/> Finalizza stagione
            </button>
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white flex items-center gap-2">
            <Sparkles className="text-amber-400" size={22}/> Gestione Sponsor
          </h2>
          <LeagueSponsorsAdmin leagueId={league.league_id}/>
        </div>
      )}

      <FinalizeSeasonDialog leagueId={league.league_id} open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        onDone={reload}/>

      <RefreshRoseDialog leagueId={league.league_id} open={refreshOpen}
        onClose={() => setRefreshOpen(false)}/>

      <SnapshotsDialog leagueId={league.league_id} open={snapshotsOpen}
        onClose={() => setSnapshotsOpen(false)} onRestored={reload}/>

      <ModuliRegoleDialog leagueId={league.league_id} open={moduliOpen}
        onClose={() => setModuliOpen(false)}/>

      <AutoSnapshotDialog open={autoSnapshotOpen}
        leagueId={league.league_id}
        season={league.season}
        currentPhase={phase}
        nextPhaseLabel={`Fase ${nextPhase}`}
        onClose={() => setAutoSnapshotOpen(false)}
        onConfirm={() => doAdvance()}/>
    </div>
  );
}

function Tile({ icon: Icon, color, title, desc, onClick, testid }) {
  return (
    <button onClick={onClick}
      data-testid={testid}
      className="group text-left rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 hover:from-slate-800 hover:to-slate-900 hover:border-slate-600 transition-all p-4 relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10 blur-2xl pointer-events-none"
        style={{ background: color }}/>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 border"
        style={{ background: `${color}22`, borderColor: `${color}55` }}>
        <Icon size={20} style={{ color }}/>
      </div>
      <div className="font-black text-white text-base tracking-tight">{title}</div>
      <div className="text-xs text-slate-400 mt-0.5 leading-relaxed">{desc}</div>
      <ArrowRight size={14} className="text-slate-600 group-hover:text-white group-hover:translate-x-1 transition-all absolute bottom-4 right-4"/>
    </button>
  );
}

// -----------------------------------------------------------------
// RefreshRoseDialog — Aggiorna Rose da Listone Fantacalcio.it
// Macro-shortcut: apre file picker, invia il listone all'endpoint
// /imports/quotazioni?mode=update_current. Ritorna breakdown changes.
// -----------------------------------------------------------------
function RefreshRoseDialog({ leagueId, open, onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  const doPreview = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setBusy(true); setPreview(null); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/leagues/${leagueId}/imports/quotazioni?mode=update_current&preview=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(r.data);
    } catch (e) { setError(formatApiError(e)); toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setBusy(true); setResult(null); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/leagues/${leagueId}/imports/quotazioni?mode=update_current`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(r.data);
      setPreview(null);
      const isOfficial = r.data.source === "listone_ufficiale";
      toast.success(`${isOfficial ? "🏆 Listone ufficiale" : "✅ Import"}: ${r.data.updated} rose aggiornate`);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setResult(null); setPreview(null); setError(null); };
  const close = () => { reset(); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="refresh-rose-dialog">
      <div className="bg-slate-950 border border-emerald-500/40 rounded-3xl w-full max-w-2xl shadow-2xl shadow-emerald-500/20 p-6 sm:p-8"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <RefreshCcw className="text-emerald-400" size={22}/>
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-tight">Aggiorna Rose da Listone</h2>
              <p className="text-xs text-slate-400 mt-1">Un click per rifreshare Q.A., ruoli Mantra e FVM di tutti i giocatori</p>
            </div>
          </div>
          <button onClick={close} data-testid="refresh-rose-close"
            className="text-slate-500 hover:text-white transition-colors">
            <X size={20}/>
          </button>
        </div>

        <div className="text-xs text-slate-300 leading-relaxed border-l-2 border-emerald-400/60 pl-3 mb-4 bg-emerald-500/5 py-2 rounded-r-md">
          💡 <strong className="text-emerald-300">Come funziona</strong>: carica l&apos;Excel del listone ufficiale <em>Fantacalcio.it 2026/27</em> (o compatibile). L&apos;app riconosce automaticamente il formato,
          aggiorna Q.A. e Q.I. Mantra di ogni giocatore in rosa, salva il <strong>fanta_id</strong> per il matching stabile e mostra le <strong>freccette ↗/↘</strong> di scostamento nella pagina Rosa.
        </div>

        {!result && !error && (
          <>
            <div className="border border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0])}
                className="hidden" id="refresh-rose-file" data-testid="refresh-rose-file"/>
              <label htmlFor="refresh-rose-file" className="cursor-pointer">
                <UploadCloud size={40} className="mx-auto text-slate-500 mb-2"/>
                <div className="font-bold text-emerald-400">{file ? file.name : "Clicca per scegliere il listone"}</div>
                <div className="text-xs text-slate-500 mt-1">{file ? `${(file.size/1024).toFixed(1)} KB` : "Es. Quotazioni_Fantacalcio_Stagione_2026_27.xlsx"}</div>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <button onClick={doPreview} disabled={busy || !file}
                data-testid="refresh-rose-preview"
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-black uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed">
                🔍 Anteprima Diff
              </button>
              <button onClick={submit} disabled={busy || !file}
                data-testid="refresh-rose-submit"
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-gradient-to-br from-emerald-400 to-emerald-600 hover:brightness-110 text-slate-950 font-black uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30">
                <Upload size={16}/> {busy ? "..." : "🚀 Applica"}
              </button>
            </div>

            {preview && (
              <div className="mt-4 border border-amber-500/40 bg-amber-500/5 rounded-lg p-3" data-testid="refresh-rose-preview-result">
                <div className="text-xs font-black text-amber-300 uppercase tracking-widest mb-2">🔍 Anteprima (non ancora applicata)</div>
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div className="bg-slate-900/60 rounded p-2">
                    <div className="text-amber-300 font-mono font-black text-xl">{preview.would_update}</div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">Da aggiornare</div>
                  </div>
                  <div className="bg-slate-900/60 rounded p-2">
                    <div className="text-cyan-300 font-mono font-black text-xl">{preview.matched_by_id || 0}</div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">Match by Id</div>
                  </div>
                  <div className="bg-slate-900/60 rounded p-2">
                    <div className="text-rose-300 font-mono font-black text-xl">{preview.not_found_count || 0}</div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">Non trovati</div>
                  </div>
                </div>
                {preview.changes_preview_sample?.length > 0 && (
                  <details className="text-xs" open>
                    <summary className="cursor-pointer text-amber-300 font-bold">Top {preview.changes_preview_sample.length} modifiche</summary>
                    <div className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1">
                      {preview.changes_preview_sample.map((c, i) => (
                        <div key={i} className="font-mono text-[11px] bg-slate-900/50 rounded p-1.5">
                          <span className="text-white font-bold">{c.name}</span>
                          <span className="text-slate-500 mx-1">·</span>
                          <span className="text-amber-300">{Object.keys(c.changes).join(", ")}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                <div className="text-[10px] text-slate-500 mt-2 italic">
                  Se ti convince, clicca 🚀 Applica per commit. Se qualcosa non torna, cambia file o annulla.
                </div>
              </div>
            )}
          </>
        )}

        {result && (
          <div className="space-y-4" data-testid="refresh-rose-result">
            <div className="flex items-center gap-2 text-emerald-300 font-bold text-lg">
              <CheckCircle2 size={20}/> Import completato
              {result.source === "listone_ufficiale" && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 tracking-widest">
                  🏆 LISTONE UFFICIALE
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
                <div className="text-slate-100 font-mono font-black text-2xl">{result.total_rows}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Righe file</div>
              </div>
              <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3">
                <div className="text-emerald-300 font-mono font-black text-2xl">{result.updated}</div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mt-1">Aggiornati</div>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/40 rounded-lg p-3">
                <div className="text-cyan-300 font-mono font-black text-2xl">{result.matched_by_id || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 mt-1">Match Id 🎯</div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-3">
                <div className="text-amber-300 font-mono font-black text-2xl">{result.not_found_count || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 mt-1">Non trovati</div>
              </div>
            </div>

            {result.changes && (
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
                {[
                  { key: "qa", label: "Q.A.", color: "cyan" },
                  { key: "qi", label: "Q.I.", color: "cyan" },
                  { key: "role_mantra", label: "Mantra", color: "purple" },
                  { key: "role_classic", label: "Classic", color: "purple" },
                  { key: "team", label: "Squadra", color: "amber" },
                  { key: "fvm", label: "FVM", color: "cyan" },
                ].map(k => (
                  <div key={k.key} className="bg-slate-900/60 border border-slate-800 rounded p-2">
                    <div className={`text-${k.color}-300 font-mono font-black text-lg`}>{result.changes[k.key] || 0}</div>
                    <div className="text-[9px] uppercase tracking-widest text-slate-500">{k.label}</div>
                  </div>
                ))}
              </div>
            )}

            {result.updated === 0 && result.changes && Object.values(result.changes).every(v => !v) && (
              <div className="border border-slate-700 bg-slate-800/40 rounded-lg p-3 text-xs text-slate-400 text-center">
                ℹ️ Nessuna variazione rilevata — le rose sono già allineate al listino ufficiale.
              </div>
            )}

            {result.suggestions?.length > 0 && (
              <UnresolvedResolver leagueId={leagueId} suggestions={result.suggestions}
                unresolvedTotal={result.unresolved_count}/>
            )}

            <div className="flex gap-2 justify-end">
              <button onClick={reset} data-testid="refresh-rose-reset"
                className="px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-sm">
                Nuovo import
              </button>
              <button onClick={close} data-testid="refresh-rose-done"
                className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm">
                Fatto
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 border border-rose-500/40 bg-rose-500/10 rounded-lg p-4 text-sm text-rose-300">
            ❌ {error}
            <button onClick={reset} className="ml-2 text-rose-200 hover:text-white underline text-xs">Riprova</button>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// UnresolvedResolver — mostra i giocatori senza fanta_id con candidati fuzzy.
// L'admin sceglie il candidato giusto → chiama attach-fanta-id → il match
// diventa permanente per i prossimi import.
// -----------------------------------------------------------------
function UnresolvedResolver({ leagueId, suggestions, unresolvedTotal }) {
  const [items, setItems] = useState(suggestions);
  const [applying, setApplying] = useState({});

  const apply = async (playerId, candidate, rosterName) => {
    setApplying(a => ({ ...a, [playerId]: true }));
    try {
      const shouldRename = candidate.name && candidate.name !== rosterName;
      const body = { fanta_id: candidate.fanta_id };
      if (shouldRename) body.name = candidate.name;
      if (candidate.squadra) body.real_team = candidate.squadra;
      if (candidate.rm) {
        body.roles_mantra = candidate.rm.split(/[;,\s]+/).filter(Boolean).map(s => s.toUpperCase());
      }
      await api.post(`/leagues/${leagueId}/players/${playerId}/attach-fanta-id`, body);
      toast.success(`✅ ${rosterName} → ${candidate.name} (fanta_id ${candidate.fanta_id})`);
      setItems(prev => prev.filter(i => i.player_id !== playerId));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(a => { const c = { ...a }; delete c[playerId]; return c; });
    }
  };

  if (!items.length) return null;

  return (
    <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-4" data-testid="unresolved-resolver">
      <div className="flex items-start gap-2 mb-3">
        <div className="text-amber-300 font-bold text-sm">
          ✋ {unresolvedTotal} giocatori in rosa senza collegamento al listone
          <span className="text-slate-400 font-normal ml-1">(mostro i {items.length} più risolvibili)</span>
        </div>
      </div>
      <div className="text-[11px] text-slate-400 mb-3 leading-relaxed">
        Clicca il candidato giusto per confermare il collegamento al Fantacalcio Id. Il match diventa permanente
        dai prossimi import: non dovrai più risolvere lo stesso nome.
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {items.map(item => (
          <div key={item.player_id} className="bg-slate-900/60 border border-slate-800 rounded-lg p-2.5" data-testid={`resolver-row-${item.player_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300 font-black uppercase">{item.role || "?"}</span>
              <span className="font-bold text-white">{item.roster_name}</span>
              <span className="text-xs text-slate-500">· {item.real_team || "—"}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {item.candidates.map(c => (
                <button key={c.fanta_id}
                  disabled={!!applying[item.player_id]}
                  onClick={() => apply(item.player_id, c, item.roster_name)}
                  data-testid={`resolver-pick-${item.player_id}-${c.fanta_id}`}
                  title={`Similarity ${Math.round(c.similarity * 100)}%`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold transition-all
                    ${c.similarity >= 0.85 ? "bg-emerald-500/15 border border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/25"
                      : c.similarity >= 0.75 ? "bg-amber-500/15 border border-amber-500/50 text-amber-200 hover:bg-amber-500/25"
                      : "bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700"}
                    disabled:opacity-40 disabled:cursor-wait`}>
                  <span className="font-black">{c.name}</span>
                  <span className="text-[10px] opacity-75">{c.squadra}</span>
                  <span className="text-[9px] font-mono ml-1 opacity-70">{Math.round(c.similarity * 100)}%</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



// -----------------------------------------------------------------
// SnapshotsDialog — lista snapshot rose disponibili + button ripristino.
// -----------------------------------------------------------------
function SnapshotsDialog({ leagueId, open, onClose, onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState({});
  const [confirmId, setConfirmId] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/leagues/${leagueId}/snapshots`);
        setItems(r.data.snapshots || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [open, leagueId]);

  const restore = async (snapId) => {
    setRestoring(s => ({ ...s, [snapId]: true }));
    setConfirmId(null);
    try {
      const r = await api.post(`/leagues/${leagueId}/snapshots/${snapId}/restore`);
      toast.success(`♻️ Ripristino OK: ${r.data.restored_players} giocatori · ${r.data.teams_updated} team. Backup pre-restore: ${r.data.counter_snapshot_id}`);
      const rr = await api.get(`/leagues/${leagueId}/snapshots`);
      setItems(rr.data.snapshots || []);
      onRestored?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRestoring(s => { const c = { ...s }; delete c[snapId]; return c; }); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="snapshots-dialog">
      <div className="bg-slate-950 border border-purple-500/40 rounded-3xl w-full max-w-3xl max-h-[85vh] shadow-2xl shadow-purple-500/20 p-6 sm:p-8 overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
              <RotateCcw className="text-purple-400" size={22}/>
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-tight">Backup & Ripristino Rose</h2>
              <p className="text-xs text-slate-400 mt-1">Snapshot creato automaticamente prima di ogni import — ripristinabile in un click</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="snapshots-close"
            className="text-slate-500 hover:text-white transition-colors">
            <X size={20}/>
          </button>
        </div>

        <div className="text-xs text-slate-300 leading-relaxed border-l-2 border-purple-400/60 pl-3 mb-4 bg-purple-500/5 py-2 rounded-r-md">
          🛡️ <strong className="text-purple-300">Come funziona</strong>: ogni import (Quotazioni o Rinnovi) crea uno snapshot completo prima di applicare le modifiche.
          Se un import va storto, clicca <strong>Ripristina</strong> e la lega torna al precedente stato. Anche il ripristino genera un contro-snapshot, così ogni azione è revertibile.
        </div>

        {loading && <div className="text-center text-slate-400 py-8">Caricamento…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center text-slate-500 py-8 text-sm">
            Nessuno snapshot. Fai un import per crearne uno.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map(s => {
              const isRestoring = !!restoring[s.snapshot_id];
              const isPreRestore = String(s.reason || "").startsWith("pre_restore_of_");
              // Iter 68: label friendly per pre-import
              const reasonRaw = String(s.reason || "");
              let friendly = reasonRaw.replace(/_/g, " ") || "manuale";
              let importIcon = "🛡️";
              if (reasonRaw.startsWith("pre_import_quotazioni")) { friendly = "Pre-Import Quotazioni Listone"; importIcon = "📥"; }
              else if (reasonRaw === "pre_import_rinnovi") { friendly = "Pre-Import Rinnovi Excel"; importIcon = "📝"; }
              else if (reasonRaw === "pre_import_rose_fantacalcio") { friendly = "Pre-Import Rose Fantacalcio CSV"; importIcon = "🌐"; }
              else if (reasonRaw === "pre_import_post_calendario") { friendly = "Pre-Import Post-Calendario XLSX"; importIcon = "📅"; }
              else if (reasonRaw === "pre_bulk_resolve_fanta_id") { friendly = "Pre-Bulk Resolve Fanta ID"; importIcon = "🩺"; }
              else if (reasonRaw === "pre_ai_resolve_fanta_id") { friendly = "Pre-AI Resolve Fanta ID"; importIcon = "🤖"; }
              else if (isPreRestore) { friendly = "Contro-snapshot ripristino"; importIcon = "♻️"; }
              return (
                <div key={s.snapshot_id}
                  data-testid={`snapshot-row-${s.snapshot_id}`}
                  className={`p-3 rounded-lg border ${isPreRestore ? "bg-amber-500/5 border-amber-500/30" : "bg-slate-900/60 border-slate-800"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs text-slate-500">{s.snapshot_id}</div>
                      <div className="font-bold text-sm text-white truncate">
                        {importIcon} {friendly}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5 font-mono">
                        {new Date(s.created_at).toLocaleString("it-IT")} · {s.players_count} giocatori · {s.teams_count} team · fase {s.season_phase}
                      </div>
                    </div>
                    {confirmId === s.snapshot_id ? (
                      <div className="flex gap-1">
                        <button onClick={() => restore(s.snapshot_id)} disabled={isRestoring}
                          data-testid={`snapshot-confirm-${s.snapshot_id}`}
                          className="px-3 py-1.5 rounded bg-rose-500 hover:bg-rose-400 text-white font-bold text-xs">
                          {isRestoring ? "Ripristino…" : "Conferma"}
                        </button>
                        <button onClick={() => setConfirmId(null)} className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs">Annulla</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmId(s.snapshot_id)}
                        data-testid={`snapshot-restore-${s.snapshot_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-300 font-bold text-xs">
                        <RotateCcw size={12}/> Ripristina
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

